import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { findCategory } from '../data/catalog';
import styles from '../styles/BestInCategory.module.css';

function formatPrice(value, currency = 'EUR') {
    if (!Number.isFinite(Number(value))) return '';
    const symbol = currency === 'EUR' ? '€' : String(currency || '');
    return `${Number(value).toFixed(2).replace('.', ',')}${symbol}`;
}

function getDiscountLabel(deal) {
    const discount = Number(deal.discount || deal.discountPercent || 0);
    return discount > 0 ? `-${Math.round(discount)}%` : null;
}

function EditorialSection({ title, children }) {
    if (!children) return null;
    return (
        <section className={styles.editorialSection}>
            {title && <h2>{title}</h2>}
            {children}
        </section>
    );
}

function CriteriaList({ items }) {
    if (!Array.isArray(items) || items.length === 0) return null;
    return (
        <ol className={styles.criteriaList}>
            {items.map((item, index) => (
                <li key={index}>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                </li>
            ))}
        </ol>
    );
}

function TipsList({ items }) {
    if (!Array.isArray(items) || items.length === 0) return null;
    return (
        <ul className={styles.tipsList}>
            {items.map((item, index) => (
                <li key={index}>{item}</li>
            ))}
        </ul>
    );
}

function FaqList({ items }) {
    if (!Array.isArray(items) || items.length === 0) return null;
    return (
        <dl className={styles.faqList}>
            {items.map((item, index) => (
                <div key={index} className={styles.faqItem}>
                    <dt>{item.question}</dt>
                    <dd>{item.answer}</dd>
                </div>
            ))}
        </dl>
    );
}

function SubcategoryLinks({ items, categoryName }) {
    if (!Array.isArray(items) || items.length === 0) return null;
    return (
        <nav className={styles.subcategoryNav} aria-label={`Especialidades de ${categoryName}`}>
            {items.map((item, index) => (
                <Link key={index} to={item.path} className={styles.subcategoryLink}>
                    {item.name}
                </Link>
            ))}
        </nav>
    );
}

function BestInCategory() {
    const { categorySlug } = useParams();
    const category = findCategory(categorySlug);
    const [summary, setSummary] = useState(null);
    const [editorial, setEditorial] = useState(null);
    const [serverTitle, setServerTitle] = useState(null);
    const [serverDescription, setServerDescription] = useState(null);
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!category) {
            return;
        }

        let active = true;
        const controller = new AbortController();
        const path = `/mejores/${category.slug}`;

        // Si page-seo falla, las ofertas se pintan igual. Con Promise.all un
        // fallo de page-seo dejaba la pagina vacia (Soft 404 en el render de
        // Googlebot mientras robots.txt la bloqueaba, 2026-10-25).
        Promise.allSettled([
            fetch(`/api/page-seo?path=${encodeURIComponent(path)}`, { signal: controller.signal })
                .then((response) => (response.ok ? response.json() : Promise.reject(new Error('page-seo unavailable')))),
            fetch(`/api/deals?category=${encodeURIComponent(category.slug)}&sort=heat&limit=20`, { signal: controller.signal })
                .then((response) => (response.ok ? response.json() : Promise.reject(new Error('deals unavailable'))))
        ])
            .then(([seoResult, dealsResult]) => {
                const seoData = seoResult.status === 'fulfilled' ? seoResult.value : null;
                const dealsData = dealsResult.status === 'fulfilled' ? dealsResult.value : null;
                if (!active) {
                    return;
                }
                if (seoData?.seo) {
                    setServerTitle(seoData.seo.title);
                    setServerDescription(seoData.seo.description);
                    setSummary(seoData.summary || null);
                }
                if (seoData?.editorial) {
                    setEditorial(seoData.editorial);
                }
                if (dealsData?.deals) {
                    setDeals(dealsData.deals);
                } else if (Array.isArray(dealsData)) {
                    setDeals(dealsData);
                }
                setLoading(false);
            })
            .catch(() => {
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
            controller.abort();
        };
    }, [category]);

    useEffect(() => {
        if (serverTitle) {
            document.title = serverTitle;
        }
        const metaDescription = document.querySelector('meta[name="description"]');
        if (serverDescription && metaDescription) {
            metaDescription.setAttribute('content', serverDescription);
        }
    }, [serverTitle, serverDescription]);

    if (!category) {
        return <Navigate to="/" replace />;
    }

    // El servidor ya inyecta el summary; el cliente lo repite para SPA.
    const intro = summary
        ? summary
        : `Hemos comparado las ofertas activas de ${category.name.toLowerCase()} y estas son las que mas merecen la pena hoy segun descuento, precio y rotacion.`;

    return (
        <main className={styles.page}>
            <section className={styles.hero}>
                <p className={styles.kicker}>Comparativa 2026</p>
                <h1>Mejores {category.name} 2026</h1>
                <p>{intro}</p>
                <div className={styles.links}>
                    <Link to={`/chollos/${category.slug}`}>Ver todas las ofertas de {category.name.toLowerCase()}</Link>
                </div>
            </section>

            {editorial && (
                <div className={styles.editorial}>
                    <EditorialSection>
                        {editorial.intro.map((paragraph, index) => (
                            <p key={index}>{paragraph}</p>
                        ))}
                    </EditorialSection>

                    {editorial.subcategories.length > 0 && (
                        <EditorialSection title={`Especialidades de ${category.name}`}>
                            <SubcategoryLinks items={editorial.subcategories} categoryName={category.name} />
                        </EditorialSection>
                    )}

                    {editorial.criteria && (
                        <EditorialSection title={editorial.criteria.title}>
                            <CriteriaList items={editorial.criteria.items} />
                        </EditorialSection>
                    )}

                    {editorial.tips && (
                        <EditorialSection title={editorial.tips.title}>
                            <TipsList items={editorial.tips.items} />
                        </EditorialSection>
                    )}

                    {editorial.faqs.length > 0 && (
                        <EditorialSection title="Preguntas frecuentes">
                            <FaqList items={editorial.faqs} />
                        </EditorialSection>
                    )}
                </div>
            )}

            <section className={styles.list} aria-label={`Top ofertas de ${category.name.toLowerCase()}`}>
                <h2 className={styles.listTitle}>Ranking de ofertas</h2>
                {loading ? (
                    <p className={styles.loading}>Cargando seleccion...</p>
                ) : deals.length === 0 ? (
                    <p className={styles.empty}>No hay ofertas destacadas en esta categoria ahora mismo.</p>
                ) : (
                    <ol className={styles.grid}>
                        {deals.map((deal, index) => (
                            <li key={deal.id} className={styles.card}>
                                <Link to={`/ofertas/${deal.id}`} className={styles.cardLink}>
                                    <span className={styles.position}>{index + 1}</span>
                                    {deal.image && (
                                        <img
                                            src={deal.image}
                                            alt={deal.name}
                                            loading={index < 4 ? 'eager' : 'lazy'}
                                            width="200"
                                            height="200"
                                        />
                                    )}
                                    <div className={styles.cardBody}>
                                        <h3>{deal.name}</h3>
                                        <p className={styles.store}>{deal.store}</p>
                                        <p className={styles.price}>
                                            <strong>{formatPrice(deal.price, deal.currency)}</strong>
                                            {getDiscountLabel(deal) && <span className={styles.discount}>{getDiscountLabel(deal)}</span>}
                                        </p>
                                    </div>
                                </Link>
                            </li>
                        ))}
                    </ol>
                )}
            </section>
        </main>
    );
}

export default BestInCategory;
